export interface EmbedConfig {
  partner: string;
  theme: 'light' | 'dark';
  locale: string;
  featureFlags: Record<string, boolean | number | string>;
  isEmbed: boolean;
}

const DEFAULT_CONFIG: EmbedConfig = {
  partner: 'default',
  theme: 'light',
  locale: 'en-US',
  featureFlags: {},
  isEmbed: false,
};

/**
 * True when the app is running inside a partner embed iframe.
 * Detected via the `embed=1` query param set by embed.js, with an
 * iframe-context fallback (`window.self !== window.top`).
 */
export function isEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('embed') === '1') return true;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws → we are definitely framed.
    return true;
  }
}

export function getEmbedConfig(): EmbedConfig {
  if (typeof window === 'undefined') return DEFAULT_CONFIG;

  const params = new URLSearchParams(window.location.search);
  const partner = params.get('partner') || params.get('embed') || 'default';
  const theme = (params.get('theme') as 'light' | 'dark') || 'light';
  const locale = params.get('locale') || navigator.language || 'en-US';

  // Parse feature flags from URL
  // Preserve boolean, number, and string values rather than coercing everything to boolean
  const featureFlags: Record<string, boolean | number | string> = {};
  params.forEach((value, key) => {
    if (key.startsWith('ff_')) {
      const flagKey = key.slice(3);
      if (value === 'true' || value === '1') {
        featureFlags[flagKey] = true;
      } else if (value === 'false' || value === '0') {
        featureFlags[flagKey] = false;
      } else if (/^\d+$/.test(value)) {
        featureFlags[flagKey] = parseInt(value, 10);
      } else {
        featureFlags[flagKey] = value;
      }
    }
  });

  return { partner, theme, locale, featureFlags, isEmbed: isEmbedded() };
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

if (import.meta.env.PROD && !API_BASE_URL) {
  throw new Error(
    'VITE_API_BASE_URL is required in production. Set it in your build environment (e.g., Cloudflare Pages env vars).'
  );
}
